/**
 * Spec 036 Quant types. Shapes follow echo-core 062 (strategies/runs) and the
 * Quant handoff (preview, market stream, live deployments); see `quantApi.ts`
 * for the contract assumptions. Everything here is plain data, no React.
 */

import { ApiError } from '@/app/lib/apiError'

import type { MarketSession, MarketSubscribe, StrategyViewSession, StrategyViewSubscribe } from './viewProtocol'

export type ParamValue = number | string | boolean

export type JsonSchemaType = 'integer' | 'number' | 'boolean' | 'string'

export interface ParamSchemaProp {
  type: JsonSchemaType
  default?: ParamValue
  description?: string
  /** Present on a string param that is a "choice" in strategy.toml. */
  enum?: string[]
}

/** JSON Schema object. Property order IS declaration order (form order). */
export interface ParamsSchema {
  type: 'object'
  properties: Record<string, ParamSchemaProp>
  additionalProperties: false
}

export interface Strategy {
  slug: string
  /** Nullable on the live schema (echo-core 0.63.0); fall back to the slug. */
  name: string | null
  description: string | null
  /** `null` for a strategy whose strategy.toml failed to parse. */
  params_schema: ParamsSchema | null
  error: string | null
  /** What the preview service offers for this strategy (echo-core 0.65.0); `null` when none. */
  preview: PreviewSupport | null
  /** Why the preview support could not be read, when echo-core says so. */
  preview_error: string | null
  run_count: number
  last_return_pct: number | null
  /** Spec 041 (echo-core 0.67.0): live-session support; `null` when none. Possibly absent. */
  live: LiveSupport | null
  /** Spec 041: why the live support could not be read, when echo-core says so. */
  live_error: string | null
}

/** A strategy's `preview` block: instruments, timeframes and the bar cap per timeframe. */
export interface PreviewSupport {
  instruments: string[]
  timeframes: string[]
  max_bars: Record<string, number>
}

export type RunStatus = 'running' | 'completed' | 'failed' | 'canceled' | 'orphaned'

const KNOWN_RUN_STATUSES: readonly string[] = ['running', 'completed', 'failed', 'canceled', 'orphaned']

/** True for the five statuses this UI knows; the wire may carry others. */
export function isKnownRunStatus(status: string): status is RunStatus {
  return KNOWN_RUN_STATUSES.includes(status)
}

/** There is no `queued` status and no `pid`: runs are never queued (062). */
export interface Run {
  id: string
  strategy_slug: string
  params: Record<string, ParamValue>
  /**
   * Typed from the server's `RunStatus` (spec 069 CQ-4). `isKnownRunStatus`
   * still guards a future value arriving over the wire.
   */
  status: RunStatus
  /** Nullable on the live schema and not required. */
  created_at: string | null
  started_at: string | null
  finished_at: string | null
  exit_code: number | null
  container_name: string | null
  error: string | null
  final_equity: number | null
  return_pct: number | null
  trades: number | null
  equity_sparkline: number[] | null
  has_chart: boolean
  /** ISO time the server recorded a Cancel request, or null. Set while the run still reads `running`. */
  cancel_requested_at: string | null
  /**
   * Live since echo-core 0.64.0 (the `Run` and `RunDetail` schemas): the run's
   * stored metrics, or null (while running, for canceled/failed/orphaned runs,
   * for strategies that record nothing and for every run finished before
   * 0.64.0). Optional here so an older server (no key at all) still works.
   */
  metrics: RunMetrics | null
}

/**
 * The single-run shape (`GET /quant/runs/{id}`, start and cancel responses):
 * `Run` plus the stored equity curve. Never on list items.
 */
export interface RunDetail extends Run {
  /** `[UTC epoch seconds, equity]`, ascending, at most 1000 points, or null. Optional for older servers. */
  equity_curve: Array<[number, number]> | null
}

/**
 * echo-core 0.64.0 `RunMetrics` (live schema). When the object is present it
 * has all 14 known keys (a value is null when the strategy could not compute
 * it) and may carry extra keys, which pass through unread.
 */
export interface RunMetrics {
  starting_balance: number | null
  wins: number | null
  losses: number | null
  /** Percent units, like `return_pct`. */
  win_rate_pct: number | null
  /** Account currency. */
  avg_win: number | null
  /** Account currency, negative. */
  avg_loss: number | null
  /** Null when there are no losses. */
  profit_factor: number | null
  /** Positive, percent of the peak. */
  max_drawdown_pct: number | null
  /** Positive, account currency. */
  max_drawdown_abs: number | null
  commission_total: number | null
  /** Null on short runs. */
  sharpe: number | null
  /** e.g. `daily_returns_annualized_252`. */
  sharpe_basis: string | null
  /** UTC epoch seconds. */
  first_trade_at: number | null
  last_trade_at: number | null
  /** Extra keys echo-quant adds pass through. */
  [extra: string]: unknown
}

export interface QuantConfig {
  jupyter_url: string
  max_concurrent_runs: number
}

/** Fixed marker detail of a QuantApiError raised by the caller's own abort. */
export const QUANT_ABORT_DETAIL = 'Request aborted.'

/**
 * Builds the `ApiError` for a failure that has a sentence and a code but no
 * parsed body: the client-side quant failures and the preview WebSocket frame.
 */
export function quantError(status: number, detail: string, code: string | null = null, body: unknown = null): ApiError {
  return new ApiError(status, body, { code, message: detail })
}

// ---- Chart vocabulary (the `chart.json` message protocol) -----------------

export interface CandlePoint {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume?: number
}

export interface ValuePoint {
  time: number
  value: number
}

export type SeriesType = 'candlestick' | 'line' | 'histogram'

export interface SeriesSpec {
  id: string
  pane: string
  type: SeriesType
  color?: string | null
  title?: string | null
  scale?: [number, number] | null
  height?: number | null
  points?: Array<CandlePoint | ValuePoint>
}

export interface ChartMarker {
  time: number
  position?: 'aboveBar' | 'belowBar' | 'inBar'
  shape?: string
  color?: string
  text?: string
  size?: number
}

export interface ChartPriceLevel {
  id?: string
  series?: string
  price: number
  time_start: number
  /** `null` = still active as of the last bar. */
  time_end: number | null
  color?: string
  style?: 'solid' | 'dashed' | string
  title?: string
}

export type ChartZoneState = 'active' | 'inverted' | 'filled' | string

export interface ChartZone {
  id?: string
  series?: string
  time_start: number
  time_end: number
  price_low: number
  price_high: number
  color?: string
  state?: ChartZoneState
  title?: string
}

export interface ChartCircle extends ChartZone {
  text?: string
}

export type ChartMessage =
  | { type: 'spec'; panes: SeriesSpec[] }
  | { type: 'append'; series: string; points: Array<CandlePoint | ValuePoint> }
  | { type: 'markers'; series: string; markers: ChartMarker[] }
  | { type: 'price_levels'; series: string; levels: ChartPriceLevel[] }
  | { type: 'zones'; series: string; zones: ChartZone[] }
  | { type: 'circles'; series: string; circles: ChartCircle[] }
  | { type: 'add_series'; series: SeriesSpec }
  | { type: 'remove_series'; series: string }
  | { type: 'remove_pane'; pane: string }
  | { type: 'clear' }

/** One series after replay. */
export interface ChartSeries {
  id: string
  pane: string
  type: SeriesType
  color: string | null
  title: string | null
  height: number
  points: Array<CandlePoint | ValuePoint>
}

/** The folded result of `replayChart`. */
export interface ChartModel {
  /** Pane names in first-seen order of series `pane`. */
  panes: string[]
  /** Series in insertion order. */
  series: ChartSeries[]
  markers: Record<string, ChartMarker[]>
  levels: Record<string, ChartPriceLevel[]>
  zones: Record<string, ChartZone[]>
  circles: Record<string, ChartCircle[]>
}

// ---- Later phases: live preview, market stream, deployments ---------------

export interface PreviewSubscribe {
  slug: string
  instrument: string
  tf: string
  bars: number
  params: Record<string, ParamValue>
}

export interface OverlayLine {
  id: string
  color: string
  /** Time-keyed points (warm-up bars are simply absent). */
  points: ValuePoint[]
}

export interface OverlayLevel {
  time_start: number
  time_end: number | null
  price: number
  color: string
  dashed: boolean
  label?: string
}

export interface OverlayZone {
  time_start: number
  time_end: number
  hi: number
  lo: number
  color: string
  alpha: number
  /** `active`, `inverted`, `filled` or a future value (echo-core 0.65.0). */
  state?: 'active' | 'inverted' | 'filled' | string
  label?: string
}

export interface OverlayMarker {
  time: number
  above: boolean
  kind: 'entry' | 'sweep' | 'skip' | 'roll' | 'candidate_long' | 'candidate_short' | string
  text: string
}

export interface OverlaySession {
  time_start: number
  time_end: number
}

export interface PreviewOverlay {
  rev: number
  legend: string[]
  stats: Array<[string, number | string]>
  lines: OverlayLine[]
  zones: OverlayZone[]
  levels: OverlayLevel[]
  markers: OverlayMarker[]
  sessions: OverlaySession[]
}

/** `GET /quant/data` row: one instrument's stored 1-minute coverage. */
export interface InstrumentCoverage {
  instrument: string
  symbol: string
  first_day: string
  last_complete_day: string | null
  /** Spec 073: the span a backtest can actually read (trades, 1s and 1m ledgers); `null` falls back to the 1m fields. */
  backtest_first_day: string | null
  backtest_last_day: string | null
  complete: number
  empty: number
  partial: number
  missing: number
  last_fetched_at: string | null
  /** Spec 040: complete days recorded live (not displayed). */
  live_days: number
  /**
   * Spec 040: read from a file, so it can stay true for a day or two after the
   * recorder process dies. Only meaningful beside a feed state that says the
   * process is up.
   */
  recording: boolean
  /** Spec 040: never displayed as a time (it can be earlier than the feed's `last_bar_written_at`). */
  recording_through: string | null
  /** Spec 041: when live recording began (not displayed). */
  recording_since: string | null
}

/**
 * Spec 040: the feed states the client renders. The wire `state` is a plain
 * string (see `FeedStatus`); `normalizeFeedState` maps it onto this union.
 */
export type FeedState =
  | 'live'
  | 'connecting'
  | 'closed'
  | 'down'
  | 'error'
  | 'unknown'
  | 'unavailable'
  | 'not_configured'

/** `FeedStatus.symbols` entry. */
export interface FeedSymbol {
  symbol: string
  instrument: string
  contract: string | null
}

/** `FeedStatus.health`. */
export interface FeedHealth {
  reconnects: number
  recording_gap: boolean
  completion_pending: boolean
  completion_refused: boolean
  trades_unrecoverable_from: string | null
  publishing_ok: boolean | null
}

/**
 * `GET /quant/feed` (echo-core 0.66.0). The route always answers 200, even when
 * the feed is down. Input 01 said every key is always present; the live schema
 * requires only `state`, so every other key is optional here. `state` is a
 * `string` on purpose: the wire may carry a value this client does not know.
 */
export interface FeedStatus {
  state: string
  code: string | null
  provider_state: string | null
  resumes_at: string | null
  last_error: string | null
  last_trade_at: string | null
  last_bar_open_at: string | null
  last_bar_written_at: string | null
  alive: boolean
  catching_up: boolean
  symbols: FeedSymbol[]
  health: FeedHealth | null
  /** Spec 070: instruments the running market process publishes; `null` = unknown (never "none"). */
  live_instruments: string[] | null
}

/** The Manual pair list built from the feed (spec 070): `known` is false when the feed did not say. */
export interface LiveSymbols {
  symbols: SymbolInfo[]
  known: boolean
  feedState: string
}

/** `GET /quant/data`. */
/** `GET /quant/instrument-sources` (spec 073): data-source label per bare instrument name. */
export interface InstrumentSources {
  sources: Record<string, string>
  /** The label for an instrument the server has no mapping for. */
  fallback: string
}

export interface DataCoverage {
  dataset: string
  schema: string
  instruments: InstrumentCoverage[]
}

export interface PreviewInputRange {
  from: string
  to: string
}

/** Body of `POST /quant/strategies/{slug}/preview`. Unknown top-level keys are a 422. */
export interface PreviewRequestBody {
  instrument: string
  tf: string
  bars?: number | null
  params?: Record<string, ParamValue>
  include_candles?: boolean
}

/** The preview answer. `candles` is absent (or null) when `include_candles` was false. */
export interface PreviewResponse {
  contract_version: number
  instrument: string
  tf: string
  data_through: number
  input_range: PreviewInputRange
  candles: CandlePoint[] | null
  overlay: PreviewOverlay
}

export type PreviewFrame =
  | { type: 'status'; state: string }
  | { type: 'meta'; instrument: string; tf: string; data_through: number; input_range: PreviewInputRange }
  | { type: 'candles'; seq: number; total: number; candles: CandlePoint[] }
  | ({ type: 'overlay'; seq: number } & PreviewOverlay)
  | {
      type: 'error'
      message: string
      code: string | null
      status: number
      missing_days?: string[]
      missing_count?: number
    }

export interface PreviewSession {
  onFrame(cb: (frame: PreviewFrame) => void): () => void
  sendParams(params: Record<string, ParamValue>): void
  close(): void
}

export type IndicatorKind = 'line' | 'band' | 'levels' | 'zones' | 'markers' | 'sessions' | string

export interface IndicatorParamDef {
  name: string
  label: string
  type: 'int' | 'float' | 'choice' | 'bool'
  default: ParamValue
  options?: string[]
  min?: number
  max?: number
}

export interface IndicatorType {
  type: string
  label: string
  group: string
  kind: IndicatorKind
  description: string
  params: IndicatorParamDef[]
}

export interface SymbolInfo {
  symbol: string
  description: string
  group: string
  venue: string
  digits: number
}

/** One indicator the client asks the stream to compute (handoff section 5). */
export interface MarketIndicatorSpec {
  id: string
  type: string
  params: Record<string, ParamValue>
  color?: string
}

/** The overlay fields one indicator frame carries (times, never indexes). */
export interface IndicatorOverlay {
  lines: OverlayLine[]
  zones: OverlayZone[]
  levels: OverlayLevel[]
  markers: OverlayMarker[]
  sessions: OverlaySession[]
}

// ---- Live paper sessions (spec 041, echo-core 0.67.0) ---------------------

export const KNOWN_LIVE_STATUSES = ['starting', 'running', 'stalled', 'stopping', 'stopped', 'interrupted', 'failed'] as const

/** True for the seven statuses this UI knows; the wire `status` is a free string. */
export function isKnownLiveStatus(status: string): boolean {
  return (KNOWN_LIVE_STATUSES as readonly string[]).includes(status)
}

export interface LiveAccount {
  starting_balance: number
  commission_per_contract: number
  max_contracts: number
}

export interface LiveAccountState {
  starting_balance: number | null
  balance: number | null
  equity: number | null
  realized_pnl: number | null
  commission_total: number | null
}

export interface LivePosition {
  side: string | null
  quantity: number | null
  avg_price: number | null
  unrealized_pnl: number | null
}

export interface LiveFeed {
  state: string | null
  provider_state: string | null
  reason: string | null
  raw_symbol: string | null
  entries_blocked: boolean | null
}

export interface LiveCounts {
  signals: number | null
  orders: number | null
  fills: number | null
  positions_closed: number | null
  events: number | null
}

export interface LiveEnded {
  reason: string | null
  exit_code: number | null
}

export interface LiveFinal {
  balance: number | null
  equity: number | null
  realized_pnl: number | null
  commission_total: number | null
  fills: number | null
  orders: number | null
  positions_closed: number | null
  close_basis: string | null
}

export interface LiveIgnored {
  name: string
  value: unknown
}

export interface LiveState {
  /** Free string (known: starting, warming_up, waiting_for_feed, running, stopping, ended). */
  activity: string | null
  started_at: string | null
  feed: LiveFeed
  last_bar_open_at: string | null
  last_trade_at: string | null
  last_price: number | null
  position: LivePosition
  account: LiveAccountState
  counts: LiveCounts
  ended: LiveEnded | null
}

export interface LiveSession {
  id: string
  strategy_slug: string
  instrument: string
  container_name: string
  params: Record<string, ParamValue>
  account: LiveAccount
  /** Free string; see `KNOWN_LIVE_STATUSES` and `isKnownLiveStatus`. */
  status: string
  created_at: string | null
  started_at: string | null
  ended_at: string | null
  stop_requested_at: string | null
  /** Free string. */
  end_reason: string | null
  error: string | null
  /** Known values: `flat`, `closed`, `possibly_open`, `unknown`; any other string must be tolerated. */
  end_position: string | null
  exit_code: number | null
  ignored: LiveIgnored[] | null
  final: LiveFinal | null
  state: LiveState | null
  /** `file` or `row`; typed loosely for tolerance. */
  state_source: string | null
  /** Free string (known: not_written, missing, unreadable, too_large, malformed, unsupported_version). */
  state_problem: string | null
  state_as_of: string | null
  state_age_s: number | null
}

export interface LiveSessionsEnvelope {
  sessions: LiveSession[]
  active: number
  max: number
  own_active: number
}

export interface LiveSupport {
  instrument: string
  defaults: Record<string, ParamValue>
  ignored_params: string[]
  account_defaults: LiveAccount
}

/** `GET /quant/runs/{id}/log` and `/quant/live/{id}/log`. `more` is true when the server capped the read. */
export interface LogChunk {
  data: string
  offset: number
  live: boolean
  more: boolean
}

export interface LiveEvent {
  seq: number
  /** Free string. */
  type: string
  at: string | null
  wall_at: string | null
  /** Free object. */
  data: Record<string, unknown>
}

export interface LiveEventsPage {
  events: LiveEvent[]
  next: number
  more: boolean
  live: boolean
  skipped: number
  problem: string | null
}

export interface LiveEquityPoint {
  t: string
  equity: number | null
  balance: number | null
  unrealized_pnl: number | null
  realized_pnl: number | null
  last_price: number | null
  position: number | null
  roll: boolean
  entries_blocked: boolean | null
}

export interface LiveEquity {
  points: LiveEquityPoint[]
  total: number
  truncated: boolean
  live: boolean
  problem: string | null
}

export interface StartLiveBody {
  strategy: string
  params?: Record<string, ParamValue>
  account?: LiveAccount
}

// ---- Saved configs (spec 073, `/quant/strategies/{slug}/configs`) ----------

/** A named param set; `params` is the form's raw map (ISO date strings, bools as booleans). */
export interface SavedConfig {
  id: number
  name: string
  params: Record<string, ParamValue | null>
  created_at: string
  updated_at: string
}

// ---- The seam -------------------------------------------------------------

export interface QuantApi {
  getConfig(): Promise<QuantConfig>
  listStrategies(): Promise<{ contract_version: 1; strategies: Strategy[] }>
  listRuns(): Promise<{ runs: Run[]; active: number; max: number; own_active?: number }>
  getRun(id: string): Promise<RunDetail>
  startRun(body: { strategy: string; params: Record<string, ParamValue> }): Promise<RunDetail>
  cancelRun(id: string): Promise<RunDetail>
  getRunLog(id: string, offset: number): Promise<LogChunk>
  getRunChart(id: string, bars: number, signal?: AbortSignal): Promise<ChartMessage[]>
  deleteRun(id: string): Promise<void>
  getData(signal?: AbortSignal): Promise<DataCoverage>
  getInstrumentSources(signal?: AbortSignal): Promise<InstrumentSources>
  getFeed(signal?: AbortSignal): Promise<FeedStatus>
  openPreview(args: PreviewSubscribe): PreviewSession
  listIndicatorCatalog(): Promise<IndicatorType[]>
  openMarketStream(args: MarketSubscribe): MarketSession
  openStrategyView(args: StrategyViewSubscribe): StrategyViewSession
  // Spec 041: live paper sessions (echo-core 0.67.0).
  listLiveSessions(signal?: AbortSignal): Promise<LiveSessionsEnvelope>
  getLiveSession(id: string, signal?: AbortSignal): Promise<LiveSession>
  startLiveSession(body: StartLiveBody): Promise<LiveSession>
  stopLiveSession(id: string): Promise<LiveSession>
  getLiveEvents(id: string, after: number | null, limit: number, signal?: AbortSignal): Promise<LiveEventsPage>
  getLiveLog(id: string, offset: number, signal?: AbortSignal): Promise<LogChunk>
  getLiveEquity(id: string, maxPoints: number, signal?: AbortSignal): Promise<LiveEquity>
  deleteLiveSession(id: string): Promise<void>
  // Spec 073: named saved configs per strategy.
  listSavedConfigs(slug: string, signal?: AbortSignal): Promise<{ configs: SavedConfig[] }>
  saveConfig(slug: string, name: string, params: Record<string, ParamValue | null>): Promise<SavedConfig>
  deleteConfig(slug: string, name: string): Promise<void>
}

// ---- Form-side param model ------------------------------------------------

export type ParamKind = 'int' | 'float' | 'bool' | 'choice' | 'str'

/** One form field, derived from a strategy's `params_schema` (declaration order). */
export interface ParamDef {
  name: string
  kind: ParamKind
  default: ParamValue | null
  help: string
  /** Only for `choice`. */
  options: string[]
}
